import React, { useEffect, useState } from 'react';
import { AppNav } from '../../components/layout/AppNav';
import { formatDate } from '../../utils/formatters';
import { Bell, CheckCircle2, XCircle, Clock, Check } from 'lucide-react';
import api from '../../services/api';

interface NotificationLog {
  id: string;
  type: string;
  title: string;
  body: string;
  isRead: boolean;
  createdAt: string;
}

const TYPE_ICON: Record<string, React.ReactNode> = {
  offer_accepted: <CheckCircle2 size={16} color="var(--succ)" />,
  offer_rejected: <XCircle size={16} color="var(--err)" />,
  link_expiring: <Clock size={16} color="#F59E0B" />,
};

const TYPE_BG: Record<string, string> = {
  offer_accepted: 'var(--succ-bg)',
  offer_rejected: 'var(--err-bg)',
  link_expiring: '#FFF7E8',
};

export const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<NotificationLog[]>('/notifications')
      .then((r) => setNotifications(r.data))
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  const markRead = async (id: string) => {
    await api.patch(`/notifications/${id}/read`).catch(() => undefined);
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, isRead: true } : n));
  };

  const markAllRead = async () => {
    await api.patch('/notifications/read-all').catch(() => undefined);
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Notifications" subtitle="Stay updated on your offers and activity" />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
        <div style={{ maxWidth: '720px' }}>
          <div className="page-header">
            <div>
              <div className="page-title">Notifications</div>
              {unreadCount > 0 && (
                <div style={{ fontSize: '13px', color: 'var(--text-2)', marginTop: '2px' }}>
                  {unreadCount} unread
                </div>
              )}
            </div>
            {unreadCount > 0 && (
              <button className="btn-ghost btn-sm" onClick={markAllRead}
                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Check size={13} />
                Mark all as read
              </button>
            )}
          </div>

          <div className="card">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: '12px' }}>
                  <div className="skeleton" style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div className="skeleton" style={{ height: '13px', width: '60%', marginBottom: '8px' }} />
                    <div className="skeleton" style={{ height: '12px', width: '80%' }} />
                  </div>
                </div>
              ))
            ) : notifications.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><Bell size={22} /></div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-1)', marginBottom: '4px' }}>No notifications yet</div>
                <div style={{ fontSize: '13px', color: 'var(--text-2)' }}>You'll see updates on your offers here.</div>
              </div>
            ) : (
              notifications.map((n, i) => (
                <div
                  key={n.id}
                  onClick={() => !n.isRead && markRead(n.id)}
                  style={{
                    display: 'flex',
                    gap: '12px',
                    padding: '14px 20px',
                    borderBottom: i < notifications.length - 1 ? '1px solid var(--border)' : 'none',
                    background: n.isRead ? 'var(--surface)' : 'var(--p-pale)',
                    cursor: n.isRead ? 'default' : 'pointer',
                    transition: 'background .15s',
                    alignItems: 'flex-start',
                  }}
                >
                  <div style={{
                    flexShrink: 0, width: '36px', height: '36px', borderRadius: '50%',
                    background: TYPE_BG[n.type] ?? 'var(--surface-alt)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {TYPE_ICON[n.type] ?? <Bell size={15} color="var(--text-2)" />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                      <span style={{ fontWeight: n.isRead ? 500 : 700, fontSize: '13.5px', color: 'var(--text-1)' }}>{n.title}</span>
                      <span style={{ fontSize: '12px', color: 'var(--text-3)', whiteSpace: 'nowrap', flexShrink: 0 }}>{formatDate(n.createdAt)}</span>
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-2)', marginTop: '2px', lineHeight: 1.5 }}>{n.body}</div>
                  </div>
                  {!n.isRead && (
                    <div style={{ flexShrink: 0, width: '7px', height: '7px', borderRadius: '50%', background: 'var(--p)', marginTop: '5px' }} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
